<template>
    <div class="row py-3 bt-dash">
        <div class="col-sm-9 d-flex justify-content-center">
            <div style="aspect-ratio: 1 / 1; width: 25%;">
                <img v-if="compState.imageUrl" :src="compState.imageUrl" alt="Image Preview"
                    class="img-thumbnail w-100 h-100 object-fit-cover">
                <div v-else class="img-thumbnail w-100 h-100 d-flex align-items-center justify-content-center"
                    style="background-color: #f0f0f0;">
                    <span>{{ t('NoImage') }}</span>
                </div>
            </div>
        </div>
        <div class="col-sm-3 d-flex align-items-center">
            <div class="mb-3 w-100">
                <label for="image-upload" class="form-label">{{ t('UploadImage') }}</label>
                <input class="form-control mb-3" type="file" id="image-upload" @change="previewImage" ref="fileInput">
                <button v-if="compState.imageUrl" class="btn btn-danger w-100" @click="removeImage">{{ t('RemoveImage') }}</button>
                <div v-else style="height: 35px;"></div>
            </div>
        </div>
    </div>
    <div class=" row py-3 bt-dash">
        <div class=" col-sm-9">
            <h6 class="f-w-700">{{ t('ButtonColor') }}</h6>
            <div class=" color-list-container mt-3" style="min-height: 320px;">
                <div class="text-center py-2 rounded-3 hover-pointer card mb-0 d-flex flex-column justify-content-center align-items-center"
                    style="height: 96px;" @click="onColorClick(item.color)" :style="{
                        backgroundColor: item.color, gridColumn: 'span 1 / span 1',
                        boxShadow: compState.activeColor === item.color ? 'inset 0 0 16px rgba(0, 0, 0, 0.7)' : 'none'
                    }" v-for="item in colorList" :key="item.color">
                    <h5 class="text-white">{{ t(item.name) }}</h5>
                </div>
            </div>
        </div>
        <div class=" col-sm-3 d-flex flex-column ">
            <h6 class="f-w-700 mb-3">{{ t('ButtonFontSize') }}</h6>
            <div class=" d-flex flex-column justify-content-between w-100 flex-grow-1">
                <button type="button" class="btn btn-outline-primary w-100 py-3"
                    :class="{ ' btn-primary': compState.buttonSize === 'X-Large' }" @click="setFontSize('X-Large')"
                    style="font-size: 1.5rem;">{{ t('XLarge') }}</button>
                <button type="button" class="btn btn-outline-primary w-100 py-3"
                    :class="{ ' btn-primary': compState.buttonSize === 'Large' }" @click="setFontSize('Large')"
                    style="font-size: 1.25rem;">{{ t('Large') }}</button>
                <button type="button" class="btn btn-outline-primary w-100 py-3"
                    :class="{ ' btn-primary': compState.buttonSize === 'Medium' }" @click="setFontSize('Medium')"
                    style="font-size: 1rem;">{{ t('Medium') }}</button>
                <button type="button" class="btn btn-outline-primary w-100 py-3"
                    :class="{ ' btn-primary': compState.buttonSize === 'Small' }" @click="setFontSize('Small')"
                    style="font-size: 0.875rem;">{{ t('Small') }}</button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { useDepartmentsStore } from '@/store/management/departmentsStore';

import { useI18n } from 'vue-i18n'
import { departmentsAppearanceLanguage } from '@/core/locales/departments-language';

const { t } = useI18n({
  messages: departmentsAppearanceLanguage
})

const departmentsStore = useDepartmentsStore();

const compState = reactive({
    imageUrl: '',
    buttonSize: '',
    activeColor: '',
});

const fileInput = ref<HTMLInputElement | null>(null);


const colorList = ref([
    { name: 'Red', color: '#FF0000' },
    { name: 'Green', color: '#00aa00' },
    { name: 'Blue', color: '#0000FF' },
    { name: 'Yellow', color: '#FFFF00' },
    { name: 'Cyan', color: '#00FFFF' },
    { name: 'Magenta', color: '#FF00FF' },
    { name: 'Orange', color: '#FFA500' },
    { name: 'Purple', color: '#800080' },
    { name: 'Brown', color: '#A52A2A' },
    { name: 'Pink', color: '#FFC0CB' },
    { name: 'Lime', color: '#32cd32' },
    { name: 'Teal', color: '#008080' },
]);

const activeDepartment = computed(() => {
    return departmentsStore.getActiveDepartment
})

const tempActiveDepartment = computed(() => {
    return departmentsStore.getTempActiveDepartment
})

const activeDepartmentId = computed(() => {
    return departmentsStore.getActiveDepartmentId
})

onMounted(() => {
    if (tempActiveDepartment.value?.id) {
        compState.imageUrl = tempActiveDepartment.value?.appearance?.image || '';
        compState.buttonSize = tempActiveDepartment.value?.appearance?.fontSizes || '';
        compState.activeColor = tempActiveDepartment.value?.appearance?.buttonColor || '';
    }
    console.log('tempActiveDepartment', tempActiveDepartment.value);

});

watch(activeDepartmentId, (newVal) => {
    if (newVal) {
        const department = activeDepartment.value
        compState.imageUrl = department.appearance.image;
        compState.buttonSize = department.appearance.fontSizes;
        compState.activeColor = department.appearance.buttonColor;
        if (fileInput.value) {
            fileInput.value.value = '';
        }
    }
});

watch(compState, (newVal) => {
    departmentsStore.setTempActiveDepartment({
        ...tempActiveDepartment.value,
        appearance: {
            image: compState.imageUrl,
            buttonColor: compState.activeColor,
            fontSizes: compState.buttonSize,
        }
    });
}, { deep: true });

function previewImage(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
        const reader = new FileReader();
        reader.onload = async (e: ProgressEvent<FileReader>) => {
            const imageData = e.target?.result;
            if (imageData) {
                const compressedImage = await compressImage(file);
                compState.imageUrl = compressedImage as string;
            }
        };
        reader.onerror = (error) => {
            console.error('Error reading file:', error);
        };
        reader.readAsDataURL(file);
    } else {
        compState.imageUrl = '';
    }
}

function compressImage(imageFile: File): Promise<string | ArrayBuffer | null> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event: ProgressEvent<FileReader>) => {
            const img = new window.Image();
            img.onload = () => {
                let width = img.width;
                let height = img.height;
                const maxSize = 800;
                if (width > maxSize || height > maxSize) {
                    if (width > height) {
                        height = Math.round((height * maxSize) / width);
                        width = maxSize;
                    } else {
                        width = Math.round((width * maxSize) / height);
                        height = maxSize;
                    }
                }
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Canvas context not available'));
                    return;
                }
                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);
                const quality = imageFile.size > 1000000 ? 0.7 : 0.95;
                const compressedImageData = canvas.toDataURL('image/jpeg', quality);
                resolve(compressedImageData);
            };
            img.onerror = reject;
            img.src = event.target?.result as string;
        };
        reader.onerror = reject;
        reader.readAsDataURL(imageFile);
    });
}

function removeImage() {
    compState.imageUrl = '';
    if (fileInput.value) {
        fileInput.value.value = '';
    }
}
function setFontSize(size: string) {
    compState.buttonSize = size;
}
function onColorClick(color: string) {
    compState.activeColor = color;
    const updated = {
        ...activeDepartment.value,
        appearance: {
            ...activeDepartment.value.appearance,
            buttonColor: color,
        }
    };
    const index = departmentsStore.departmentList.findIndex(
        (d) => d.id === activeDepartment.value.id
    );
    if (index !== -1) {
        departmentsStore.departmentList[index] = updated;
    }
    departmentsStore.setTempActiveDepartment(updated);
}
</script>

<style scoped lang="scss">
.btn.btn-primary {
    color: white !important;
}

.color-list-container {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1rem;
}
</style>